import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { formatConfirmation } from "@/utils/confirmation";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Bus, LogOut, Calendar, Users, UserCheck, MapPin, Bell, MessageCircle, BellRing, Lock, Truck, Plane, FileText, BarChart3, Settings, Clock, Inbox, CalendarDays
} from "lucide-react";
import { AdminInstallPrompt } from "@/components/AdminInstallPrompt";
import { AdminStatsCards } from "@/components/admin/AdminStatsCards";
import { FlightCard } from "@/components/admin/FlightCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DailyPassengersChart, DestinationCountsChart, FlightGroupingsChart } from "@/components/admin/AdminCharts";
import { BookingsTabContent } from "@/components/admin/BookingsTabContent";
import WhatsAppUsageBadge from "@/components/admin/WhatsAppUsageBadge";
import { DriversTabContent } from "@/components/admin/DriversTabContent";
import { RemindersTabContent, WhatsAppGroupsTabContent, DestinationsTabContent, ShippingTabContent } from "@/components/admin/OtherTabs";
import { DailySummaryTab } from "@/components/admin/DailySummaryTab";
import { AnalyticsTab } from "@/components/admin/AnalyticsTab";
import { SettingsTab } from "@/components/admin/SettingsTab";
import { MessagesTab } from "@/components/admin/MessagesTab";
import { CalendarTab } from "@/components/admin/CalendarTab";
import { DispatchersTab } from "@/components/admin/DispatchersTab";
import { subscribeToPush, isPushSupported, getPushPermission } from "@/utils/pushNotifications";

import getApiUrl from "@/utils/api";

const API = getApiUrl();

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [adminInfo, setAdminInfo] = useState(null);
  const [newDestination, setNewDestination] = useState({ name: "", description: "", image_url: "" });
  const [isAddingDestination, setIsAddingDestination] = useState(false);
  const [upcomingTrips, setUpcomingTrips] = useState({});
  const [sendingReminders, setSendingReminders] = useState(false);
  const [shippingRequests, setShippingRequests] = useState([]);
  const [shippingPrices, setShippingPrices] = useState({});
  const [bolSubmissions, setBolSubmissions] = useState([]);
  const [flightTracking, setFlightTracking] = useState([]);
  const [flightLoading, setFlightLoading] = useState(false);
  const [dailySummary, setDailySummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryPreview, setSummaryPreview] = useState("");
  
  // Driver management
  const [showAddDriver, setShowAddDriver] = useState(false);
  const [newDriver, setNewDriver] = useState({ name: "", phone: "", whatsapp: "", role: "Driver" });
  
  // Upload booking
  const [showUploadBooking, setShowUploadBooking] = useState(false);
  const [uploadedText, setUploadedText] = useState("");
  const [parsedBooking, setParsedBooking] = useState(null);

  // Create Custom Booking
  const [showCreateBooking, setShowCreateBooking] = useState(false);
  const [customBooking, setCustomBooking] = useState({
    full_name: "", whatsapp_number: "", email: "", travel_date: "",
    travel_time: "", trip_type: "arrival", flight_number: "", airline: "",
    pickup_location: "", dropoff_location: "", housing_unit: "",
    num_passengers: 1, total_suitcases: 0, total_carry_ons: 0, payment_method: "manual",
    payment_status: "pending", booking_status: "confirmed",
    admin_notes: "", departure_time: ""
  });
  
  // Dashboard feature toggles (from site_settings)
  const [dashFeatures, setDashFeatures] = useState({ show_flight_chart: false });

  // WhatsApp Groups
  const [whatsappGroups, setWhatsappGroups] = useState([]);
  const [newGroup, setNewGroup] = useState({ date: "", invite_link: "", description: "" });
  
  // Active tab
  const [activeTab, setActiveTab] = useState("bookings");
  
  // Change password
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: "", new: "", confirm: "" });
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState(null);
  const [paymentFilter, setPaymentFilter] = useState(null);
  const [passengerStatusFilter, setPassengerStatusFilter] = useState(null);
  const [dateFilter, setDateFilter] = useState(null);
  const [sortBy, setSortBy] = useState("earliest_arrival");
  const [groupFilter, setGroupFilter] = useState(null);
  const [driverFilter, setDriverFilter] = useState(null);
  const [terminalFilter, setTerminalFilter] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    const admin = localStorage.getItem("adminInfo");
    if (!token) { navigate("/admin"); return; }
    setAdminInfo(JSON.parse(admin));
    fetchData().then(() => { autoUpdateFlights(); });
    fetchDrivers();
    fetchWhatsAppGroups();
    // Load dashboard feature toggles
    axios.get(`${API}/admin/settings`, getAuthHeaders()).then(r => {
      const ft = r.data?.dashboard_features || {};
      setDashFeatures(prev => ({ ...prev, ...ft }));
    }).catch(() => {});
    
    // Auto-subscribe to push notifications
    if (isPushSupported()) {
      getPushPermission().then(perm => {
        if (perm === 'granted') {
          subscribeToPush('admin').then(ok => setPushEnabled(ok));
        }
      });
    }
  }, [navigate]);

  // Auto-refresh dashboard when new bookings arrive (check every 30s)
  const [lastBookingCount, setLastBookingCount] = useState(0);
  useEffect(() => {
    const interval = setInterval(async () => {
      if (!adminInfo) return;
      try {
        const res = await axios.get(`${API}/admin/bookings/count`, getAuthHeaders());
        const newCount = res.data.total || 0;
        if (lastBookingCount > 0 && newCount !== lastBookingCount) {
          fetchData();
          toast.info(`Dashboard refreshed — ${newCount > lastBookingCount ? 'new booking!' : 'booking updated'}`);
        }
        setLastBookingCount(newCount);
      } catch (e) { /* silent */ }
    }, 30000);
    return () => clearInterval(interval);
  }, [adminInfo, lastBookingCount]);

  const enablePushNotifications = async () => {
    const ok = await subscribeToPush('admin');
    setPushEnabled(ok);
    if (ok) toast.success("Push notifications enabled!");
    else toast.error("Could not enable push notifications. Please allow notifications in your browser.");
  };

  // Fetch flight statuses when bookings load
  useEffect(() => {
    if (bookings.length > 0) fetchFlightStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings.length]);


  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` }
  });

  const fetchDrivers = async () => {
    try {
      const res = await axios.get(`${API}/admin/drivers`, getAuthHeaders());
      setDrivers(res.data);
    } catch (error) { console.error("Failed to fetch drivers:", error); }
  };

  const fetchWhatsAppGroups = async () => {
    try {
      const response = await axios.get(`${API}/admin/whatsapp-groups`, getAuthHeaders());
      setWhatsappGroups(response.data || []);
    } catch (error) { console.error("Failed to fetch WhatsApp groups:", error); }
  };

  const addWhatsAppGroup = async () => {
    if (!newGroup.date || !newGroup.invite_link) { toast.error("Please fill in date and invite link"); return; }
    try {
      await axios.post(`${API}/admin/whatsapp-groups`, newGroup, getAuthHeaders());
      toast.success("WhatsApp group added!");
      setNewGroup({ date: "", invite_link: "", description: "" });
      fetchWhatsAppGroups();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to add group"); }
  };

  const deleteWhatsAppGroup = async (groupId) => {
    try {
      await axios.delete(`${API}/admin/whatsapp-groups/${groupId}`, getAuthHeaders());
      toast.success("Group deleted");
      fetchWhatsAppGroups();
    } catch (error) { toast.error("Failed to delete group"); }
  };

  const toggleDriverAvailability = async (driverId) => {
    try {
      await axios.post(`${API}/admin/drivers/${driverId}/toggle-availability`, {}, getAuthHeaders());
      fetchDrivers();
      toast.success("Driver availability updated");
    } catch (error) { toast.error("Failed to update driver"); }
  };

  const fetchData = async () => {
    try {
      const [statsRes, bookingsRes, destinationsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, getAuthHeaders()),
        axios.get(`${API}/admin/bookings`, getAuthHeaders()),
        axios.get(`${API}/admin/destinations`, getAuthHeaders())
      ]);
      setStats(statsRes.data);
      const bookingsData = bookingsRes.data;
      const allBookings = [
        ...(bookingsData.individual_bookings || []).map(b => ({ ...b, booking_type: 'individual' })),
        ...(bookingsData.group_bookings || []).map(b => ({ ...b, booking_type: 'group', full_name: b.organizer_name, whatsapp_number: b.organizer_whatsapp }))
      ];
      allBookings.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setBookings(allBookings);
      setDestinations(destinationsRes.data);
      try {
        const upcomingRes = await axios.get(`${API}/admin/upcoming-trips`, getAuthHeaders());
        setUpcomingTrips(upcomingRes.data);
      } catch (e) { console.log("Could not fetch upcoming trips"); }
      try {
        const shipRes = await axios.get(`${API}/shipping/requests`);
        setShippingRequests(shipRes.data || []);
      } catch (e) { console.log("Could not fetch shipping requests"); }
      try {
        const settingsRes = await axios.get(`${API}/admin/settings`, getAuthHeaders());
        setShippingPrices(settingsRes.data?.pricing?.shipping || {});
      } catch (e) { console.log("Could not fetch shipping prices"); }
      try {
        const bolRes = await axios.get(`${API}/shipping/bills-of-lading`, getAuthHeaders());
        setBolSubmissions(bolRes.data || []);
      } catch (e) { console.log("Could not fetch BoL submissions"); }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      if (error.response?.status === 401) { toast.error("Session expired. Please login again."); handleLogout(); }
    } finally { setLoading(false); }
  };

  // Auto-update flight statuses on initial load
  const autoUpdateFlights = async () => {
    try {
      const res = await axios.post(`${API}/admin/flights/auto-update`, {}, getAuthHeaders());
      if (res.data.updated > 0) {
        console.log(`Updated ${res.data.updated} flight statuses`);
        if (res.data.corrections?.length > 0) {
          toast.info(`Auto-corrected ${res.data.corrections.length} flight detail(s)`);
        }
        // Re-fetch bookings to get updated flight info
        fetchData();
      }
    } catch (e) { console.log("Flight auto-update skipped"); }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminInfo");
    navigate("/admin");
  };

  const [airports, setAirports] = useState([]);
  const [housingUnitsAdmin, setHousingUnitsAdmin] = useState([]);
  
  useEffect(() => {
    // Fetch airports and housing units for custom booking form
    const fetchOptions = async () => {
      try {
        const [aRes, hRes] = await Promise.all([
          axios.get(`${API}/airports`),
          axios.get(`${API}/housing-units`)
        ]);
        setAirports(aRes.data || []);
        setHousingUnitsAdmin(hRes.data || []);
      } catch (e) { console.error("Failed to fetch options", e); }
    };
    fetchOptions();
  }, []);

  const updateShippingStatus = async (requestId, status) => {
    try {
      await axios.put(`${API}/shipping/requests/${requestId}/status?status=${status}`);
      toast.success(`Shipping status updated to ${status}`);
      setShippingRequests(prev => prev.map(r => r.id === requestId ? { ...r, status } : r));
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const createCustomBooking = async () => {
    if (!customBooking.full_name || !customBooking.whatsapp_number || !customBooking.travel_date) {
      toast.error("Please fill in name, phone, and travel date");
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/bookings/custom`, customBooking, getAuthHeaders());
      toast.success(`Booking created! Confirmation: ${res.data.confirmation}`);
      setShowCreateBooking(false);
      setCustomBooking({
        full_name: "", whatsapp_number: "", email: "", travel_date: "",
        travel_time: "", trip_type: "arrival", ride_type: "shared", uses_shipping_company: false,
        flight_number: "", airline: "",
        pickup_location: "", dropoff_location: "", housing_unit: "",
        num_passengers: 1, total_suitcases: 0, total_carry_ons: 0, total_barrels: 0,
        payment_method: "manual", payment_status: "pending", booking_status: "confirmed",
        admin_notes: "", departure_time: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create booking");
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post(`${API}/admin/change-password`, { current_password: currentPassword, new_password: newPassword }, getAuthHeaders());
      toast.success("Password changed successfully!");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
      return false;
    }
  };

  // Fetch live flight statuses for today's bookings
  const fetchFlightStatuses = async () => {
    const todayBookings = bookings.filter(b => {
      const today = new Date().toISOString().split('T')[0];
      const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
      return (b.travel_date === today || b.travel_date === tomorrow) && b.flight_number;
    });
    const uniqueFlights = [...new Set(todayBookings.map(b => `${(b.flight_number||'').toUpperCase()}|${b.travel_date}`))];
    for (const key of uniqueFlights.slice(0, 10)) {
      const [flightNum, date] = key.split('|');
      try {
        const res = await axios.get(`${API}/admin/flight-status/${flightNum}?date=${date}`, getAuthHeaders());
        if (res.data.found) {
          setBookings(prev => prev.map(b => {
            if ((b.flight_number||'').toUpperCase() === flightNum && b.travel_date === date) {
              return { ...b, flight_status: res.data.status, landing_time: res.data.landing_time };
            }
            return b;
          }));
        }
      } catch (e) { /* silent */ }
    }
  };

  // Filter and sort bookings
  const getFilteredBookings = () => {
    let filtered = [...bookings];
    if (statusFilter) filtered = filtered.filter(b => b.booking_status === statusFilter);
    if (paymentFilter === "deposit_unpaid") {
      filtered = filtered.filter(b => b.booking_status !== "cancelled" && !b.deposit_verified);
    } else if (paymentFilter) {
      filtered = filtered.filter(b => b.payment_status === paymentFilter);
    }
    if (passengerStatusFilter) filtered = filtered.filter(b => b.passenger_status === passengerStatusFilter);
    if (dateFilter) filtered = filtered.filter(b => b.travel_date === dateFilter);
    if (groupFilter) filtered = filtered.filter(b => b.booking_type === 'group');
    if (driverFilter) filtered = filtered.filter(b => b.assigned_driver_name === driverFilter);
    if (terminalFilter) filtered = filtered.filter(b => b.arrival_terminal === terminalFilter);
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "date_asc": return new Date(a.travel_date) - new Date(b.travel_date);
        case "date_desc": return new Date(b.travel_date) - new Date(a.travel_date);
        case "name": return (a.full_name || "").localeCompare(b.full_name || "");
        case "amount": return (b.total_amount || 0) - (a.total_amount || 0);
        case "time": return (a.travel_time || "").localeCompare(b.travel_time || "");
        case "earliest_arrival": {
          // Today first → upcoming days ascending → past dates last.
          // Within each date, earliest travel time first.
          const today = new Date().toISOString().slice(0, 10);
          const da = a.travel_date || "";
          const db = b.travel_date || "";
          const aPast = da && da < today;
          const bPast = db && db < today;
          if (aPast !== bPast) return aPast ? 1 : -1;
          const dateCmp = da.localeCompare(db);
          if (dateCmp !== 0) return aPast ? -dateCmp : dateCmp;
          return (a.travel_time || "").localeCompare(b.travel_time || "");
        }
        default: return 0;
      }
    });
    return filtered;
  };

  const clearFilters = () => {
    setStatusFilter(null); setPaymentFilter(null); setPassengerStatusFilter(null);
    setDateFilter(null); setGroupFilter(null); setDriverFilter(null);
    setTerminalFilter(null); setSortBy("date_desc");
  };

  const hasActiveFilters = statusFilter || paymentFilter || passengerStatusFilter || dateFilter || groupFilter || driverFilter || terminalFilter;

  const confirmBooking = async (bookingId) => {
    try {
      const res = await axios.put(`${API}/admin/bookings/${bookingId}/confirm`, {}, getAuthHeaders());
      const confirmationNo = formatConfirmation(bookingId);
      const d = res.data?.delivery || {};
      const extraSent = d.extra_whatsapp_sent || 0;
      const extraFailed = d.extra_whatsapp_failed || 0;
      let desc = "";
      if (d.primary_whatsapp) desc += "WhatsApp ✓ ";
      if (d.email) desc += "Email ✓ ";
      if (extraSent) desc += `+${extraSent} co-passenger${extraSent !== 1 ? 's' : ''} notified `;
      if (extraFailed) desc += `· ${extraFailed} co-passenger WA failed`;
      toast.success(`Booking confirmed! Confirmation #: ${confirmationNo}`, {
        description: desc || "Booking status set to confirmed",
        duration: 7000
      });
      fetchData();
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || "unknown";
      toast.error(`Failed to confirm booking · ${detail}`);
    }
  };

  const cancelBooking = async (bookingId) => {
    try { await axios.put(`${API}/admin/bookings/${bookingId}/cancel`, {}, getAuthHeaders()); toast.success("Booking cancelled"); fetchData(); }
    catch (error) { toast.error("Failed to cancel booking"); }
  };

  const uncancelBooking = async (bookingId) => {
    try { await axios.put(`${API}/admin/bookings/${bookingId}/uncancel`, {}, getAuthHeaders()); toast.success("Booking restored!"); fetchData(); }
    catch (error) { toast.error("Failed to restore booking"); }
  };

  const undoPayment = async (bookingId) => {
    try { await axios.put(`${API}/admin/bookings/${bookingId}/undo-payment`, {}, getAuthHeaders()); toast.success("Payment status reset to pending"); fetchData(); }
    catch (error) { toast.error("Failed to undo payment"); }
  };

  const cashPayment = async (bookingId) => {
    try { const res = await axios.put(`${API}/admin/bookings/${bookingId}/cash-payment`, {}, getAuthHeaders()); toast.success(res.data.message || "Cash payment recorded"); fetchData(); }
    catch (error) { toast.error("Failed to record cash payment"); }
  };

  const sendMessageToPassenger = async (bookingId, message) => {
    try {
      await axios.post(`${API}/admin/bookings/${bookingId}/send-message`, { message }, getAuthHeaders());
      toast.success("Message sent via WhatsApp!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message");
    }
  };

  const fetchFlightTracking = async () => {
    setFlightLoading(true);
    try {
      const res = await axios.get(`${API}/admin/flight-tracking`, getAuthHeaders());
      setFlightTracking(res.data.flights || []);
    } catch (e) { toast.error("Failed to fetch flight data"); }
    setFlightLoading(false);
  };

  const verifyPayment = async (bookingId) => {
    try { await axios.put(`${API}/admin/bookings/${bookingId}/verify-payment`, {}, getAuthHeaders()); toast.success("Payment verified!"); fetchData(); }
    catch (error) { toast.error("Failed to verify payment"); }
  };

  const sendConfirmation = async (bookingId) => {
    try {
      const res = await axios.post(`${API}/admin/bookings/${bookingId}/send-confirmation`, {}, getAuthHeaders());
      toast.success(res.data.message || "Confirmation sent!");
    } catch (error) { toast.error("Failed to send confirmation"); }
  };

  const addDestination = async () => {
    if (!newDestination.name || !newDestination.description) { toast.error("Please fill in name and description"); return; }
    try {
      await axios.post(`${API}/admin/destinations`, newDestination, getAuthHeaders());
      toast.success("Destination added!");
      setNewDestination({ name: "", description: "", image_url: "" });
      setIsAddingDestination(false);
      fetchData();
    } catch (error) { toast.error("Failed to add destination"); }
  };

  const deleteDestination = async (destinationId) => {
    try { await axios.delete(`${API}/admin/destinations/${destinationId}`, getAuthHeaders()); toast.success("Destination removed"); fetchData(); }
    catch (error) { toast.error("Failed to delete destination"); }
  };

  const addDriver = async () => {
    if (!newDriver.name || !newDriver.phone) { toast.error("Please fill in driver name and phone"); return; }
    try {
      await axios.post(`${API}/admin/drivers`, newDriver, getAuthHeaders());
      toast.success("Driver added!");
      setNewDriver({ name: "", phone: "", whatsapp: "", role: "Driver" });
      setShowAddDriver(false);
      fetchDrivers();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to add driver"); }
  };

  const deleteDriver = async (driverId) => {
    try { await axios.delete(`${API}/admin/drivers/${driverId}`, getAuthHeaders()); toast.success("Driver deleted!"); fetchDrivers(); }
    catch (error) { toast.error(error.response?.data?.detail || "Failed to delete driver"); }
  };

  const parseBookingText = (text) => {
    const lines = text.split('\n').map(l => l.trim()).filter(l => l);
    const booking = { full_name: "", whatsapp_number: "", email: "", travel_date: "", travel_time: "", flight_number: "", airline: "", pickup_location: "Chicago O'Hare International", dropoff_location: "", housing_unit: "", num_passengers: 1, passengers: [], trip_type: "arrival", ride_type: "shared", uses_shipping_company: false, total_suitcases: 0, total_carry_ons: 0, total_barrels: 0 };
    for (const line of lines) {
      const lower = line.toLowerCase();
      if (lower.includes("name:") || lower.includes("passenger:")) { booking.full_name = line.split(":").slice(1).join(":").trim(); }
      else if (lower.includes("phone:") || lower.includes("whatsapp:") || lower.includes("number:")) { booking.whatsapp_number = line.split(":").slice(1).join(":").trim().replace(/[^\d+]/g, ""); }
      else if (lower.includes("email:") || line.includes("@")) { const m = line.match(/[\w.-]+@[\w.-]+\.\w+/); if (m) booking.email = m[0]; }
      else if (lower.includes("date:") || lower.includes("arrival:")) {
        const m = line.match(/\d{4}-\d{2}-\d{2}|\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}/);
        if (m) { let d = m[0]; if (d.includes("/")) { const p = d.split("/"); if (p[2].length === 2) p[2] = "20" + p[2]; d = `${p[2]}-${p[0].padStart(2,"0")}-${p[1].padStart(2,"0")}`; } booking.travel_date = d; }
      }
      else if (lower.includes("time:") || lower.match(/\d{1,2}:\d{2}/)) { const m = line.match(/\d{1,2}:\d{2}\s*(am|pm)?/i); if (m) { let t = m[0]; if (t.toLowerCase().includes("pm") && !t.startsWith("12")) { t = t.replace(/\d{1,2}/, (parseInt(t) + 12).toString()); } booking.travel_time = t.replace(/\s*(am|pm)/i, "").trim(); } }
      else if (lower.includes("flight:") || lower.match(/[a-z]{2}\d{3,4}/i)) { const m = line.match(/[A-Z]{2}\d{3,4}/i); if (m) booking.flight_number = m[0].toUpperCase(); }
      else if (lower.includes("airline:")) { booking.airline = line.split(":").slice(1).join(":").trim(); }
      else if (lower.includes("housing:") || lower.includes("destination:") || lower.includes("address:")) { booking.housing_unit = line.split(":").slice(1).join(":").trim(); }
      else if (lower.includes("passenger") && lower.match(/\d/)) { const m = line.match(/\d+/); if (m) booking.num_passengers = parseInt(m[0]); }
      else if (lower.includes("return") || lower.includes("departure")) { booking.trip_type = "return"; }
      else if (lower.includes("barrel")) { const m = line.match(/\d+/); if (m) booking.total_barrels = parseInt(m[0]); }
      else if (lower.includes("suitcase") || lower.includes("luggage") || lower.includes("bag")) { const m = line.match(/\d+/); if (m) booking.total_suitcases = parseInt(m[0]); }
    }
    if (booking.flight_number && !booking.airline) {
      const code = booking.flight_number.substring(0, 2).toUpperCase();
      const airlines = { UA: "United Airlines", AA: "American Airlines", DL: "Delta", NK: "Spirit", F9: "Frontier", SW: "Southwest", B6: "JetBlue" };
      booking.airline = airlines[code] || code;
    }
    if (booking.full_name && booking.whatsapp_number) { booking.passengers = [{ name: booking.full_name, whatsapp: booking.whatsapp_number }]; }
    setParsedBooking(booking);
    return booking;
  };

  const submitUploadedBooking = async () => {
    if (!parsedBooking || !parsedBooking.full_name || !parsedBooking.travel_date) { toast.error("Please fill in required fields (name, date)"); return; }
    try {
      const res = await axios.post(`${API}/admin/bookings/upload`, parsedBooking, getAuthHeaders());
      toast.success(`Booking created! Confirmation: ${res.data.confirmation}`);
      setShowUploadBooking(false); setUploadedText(""); setParsedBooking(null); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to create booking"); }
  };

  const sendAllReminders = async () => {
    setSendingReminders(true);
    try { const response = await axios.post(`${API}/admin/send-reminders`, {}, getAuthHeaders()); toast.success(`Sent ${response.data.reminders_sent} reminders!`); fetchData(); }
    catch (error) { toast.error("Failed to send reminders"); }
    finally { setSendingReminders(false); }
  };

  const sendSingleReminder = async (bookingId) => {
    try { await axios.post(`${API}/bookings/${bookingId}/send-reminder`, {}, getAuthHeaders()); toast.success("Reminder sent!"); fetchData(); }
    catch (error) { toast.error("Failed to send reminder"); }
  };

  const fetchDailySummary = async (date) => {
    setSummaryLoading(true);
    try {
      const dateParam = date || new Date().toISOString().split('T')[0];
      const res = await axios.get(`${API}/admin/daily-summary?date=${dateParam}`, getAuthHeaders());
      setDailySummary(res.data);
    } catch (e) { toast.error("Failed to fetch daily summary"); }
    setSummaryLoading(false);
  };

  const sendDailySummaryWhatsApp = async (phone) => {
    if (!phone) { toast.error("Please enter admin WhatsApp number"); return; }
    setSummaryLoading(true);
    try {
      const dateStr = dailySummary?.date || new Date().toISOString().split('T')[0];
      const res = await axios.post(`${API}/admin/daily-summary/send`, {
        date: dateStr, send_whatsapp: true, admin_phone: phone
      }, getAuthHeaders());
      toast.success(res.data.message || "Summary sent!");
      if (res.data.preview) setSummaryPreview(res.data.preview);
    } catch (e) { toast.error("Failed to send summary"); }
    setSummaryLoading(false);
  };

  const getStatusBadge = (status, type) => {
    const config = {
      booking: { pending: { variant: "secondary", text: "Pending" }, confirmed: { variant: "default", text: "Confirmed" }, cancelled: { variant: "destructive", text: "Cancelled" } },
      payment: { pending: { variant: "secondary", text: "Pending" }, pending_verification: { variant: "outline", text: "Verifying" }, paid: { variant: "default", text: "Paid" } }
    };
    const style = config[type][status] || config[type].pending;
    return <Badge variant={style.variant}>{style.text}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <AdminInstallPrompt />
      
      <header className="bg-gray-900 text-white sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 md:px-8 py-2.5 md:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm md:text-lg">ET</span>
            </div>
            <div className="min-w-0">
              <h1 className="text-sm md:text-xl font-bold truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>Easy Transport Admin</h1>
              <p className="text-[10px] md:text-xs text-white/70 truncate">Welcome, {adminInfo?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
            {/* Pending bookings badge */}
            {bookings.filter(b => b.booking_status === "pending").length > 0 && (
              <Badge
                className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30 cursor-pointer hover:bg-yellow-500/30 transition-colors text-[10px] md:text-xs px-1.5 md:px-2.5"
                onClick={() => { setActiveTab("bookings"); setStatusFilter("pending"); }}
                data-testid="pending-bookings-badge"
              >
                <Clock className="w-3 h-3 mr-0.5 md:mr-1" /> {bookings.filter(b => b.booking_status === "pending").length}<span className="hidden md:inline ml-1">Pending</span>
              </Badge>
            )}
            <WhatsAppUsageBadge getAuthHeaders={getAuthHeaders} />
            {isPushSupported() && !pushEnabled && (
              <Button variant="ghost" size="icon" onClick={enablePushNotifications} data-testid="enable-push-btn" className="text-white hover:bg-white/10 h-8 w-8 md:h-10 md:w-10" title="Enable Push Notifications">
                <BellRing className="w-4 h-4" />
              </Button>
            )}
            {pushEnabled && (
              <Badge className="bg-green-500/20 text-green-300 border-green-500/30 hidden md:inline-flex">
                <Bell className="w-3 h-3 mr-1" /> Push On
              </Badge>
            )}
            <Button variant="ghost" onClick={handleLogout} data-testid="logout-btn" className="text-white hover:bg-white/10 h-8 md:h-10 px-2 md:px-3" title="Logout">
              <LogOut className="w-4 h-4 md:mr-2" /> <span className="hidden md:inline">Logout</span>
            </Button>
            <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 h-8 w-8 md:h-10 md:w-10" data-testid="change-password-btn" title="Change Password">
                  <Lock className="w-4 h-4" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Change Password</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <div>
                    <Label>Current Password</Label>
                    <Input type="password" value={passwordForm.current} onChange={e => setPasswordForm(p => ({...p, current: e.target.value}))} data-testid="current-password" />
                  </div>
                  <div>
                    <Label>New Password</Label>
                    <Input type="password" value={passwordForm.new} onChange={e => setPasswordForm(p => ({...p, new: e.target.value}))} data-testid="new-password" />
                  </div>
                  <div>
                    <Label>Confirm New Password</Label>
                    <Input type="password" value={passwordForm.confirm} onChange={e => setPasswordForm(p => ({...p, confirm: e.target.value}))} data-testid="confirm-password" />
                  </div>
                  <Button
                    className="w-full bg-green-600 hover:bg-green-700"
                    data-testid="submit-password-btn"
                    onClick={async () => {
                      if (passwordForm.new !== passwordForm.confirm) { toast.error("Passwords don't match"); return; }
                      if (passwordForm.new.length < 6) { toast.error("New password must be at least 6 characters"); return; }
                      const ok = await changePassword(passwordForm.current, passwordForm.new);
                      if (ok) { setShowChangePassword(false); setPasswordForm({ current: "", new: "", confirm: "" }); }
                    }}
                  >Change Password</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 md:px-8 py-4 md:py-8">
        {/* Push notification prompt */}
        {isPushSupported() && !pushEnabled && (
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-testid="push-prompt-banner">
            <div className="flex items-center gap-3 min-w-0">
              <BellRing className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-amber-800">Enable Push Notifications</p>
                <p className="text-xs text-amber-600">Get instant alerts for new bookings, payments, and driver updates</p>
              </div>
            </div>
            <Button size="sm" onClick={enablePushNotifications} className="bg-amber-600 hover:bg-amber-700 text-white w-full sm:w-auto flex-shrink-0" data-testid="enable-push-banner-btn">
              <Bell className="w-4 h-4 mr-1" /> Enable
            </Button>
          </div>
        )}
        <AdminStatsCards stats={stats} onCardClick={(tab, filter) => {
          setActiveTab(tab);
          // Reset filters first
          setStatusFilter(null);
          setPaymentFilter(null);
          // Apply relevant filter
          if (filter === "pending") setStatusFilter("pending");
          else if (filter === "confirmed") setStatusFilter("confirmed");
          else if (filter === "paid") setPaymentFilter("paid");
          else if (filter === "verify") setPaymentFilter("pending_verification");
          else if (filter === "deposit_unpaid") setPaymentFilter("deposit_unpaid");
          else if (filter === "all" || filter === "today") {
            setStatusFilter(null);
            setPaymentFilter(null);
          }
        }} />
        <DailyPassengersChart stats={stats} />
        <DestinationCountsChart stats={stats} />
        {dashFeatures.show_flight_chart && <FlightGroupingsChart stats={stats} />}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 md:space-y-6">
          <TabsList className="bg-white border-2 border-gray-200 p-1.5 rounded-xl flex flex-wrap gap-1 justify-start h-auto w-full shadow-sm">
            <TabsTrigger value="bookings" className="rounded-lg data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-md text-xs md:text-sm font-medium px-2.5 md:px-3 py-1.5">
              <Calendar className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-1.5" /> Bookings
            </TabsTrigger>
            <TabsTrigger value="drivers" className="rounded-lg data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-md text-xs md:text-sm font-medium px-2.5 md:px-3 py-1.5">
              <UserCheck className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-1.5" /> Drivers
            </TabsTrigger>
            <TabsTrigger value="whatsapp" className="rounded-lg data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-md text-xs md:text-sm font-medium px-2.5 md:px-3 py-1.5">
              <MessageCircle className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-1.5" /> <span className="hidden sm:inline">WhatsApp </span>Groups
            </TabsTrigger>
            <TabsTrigger value="reminders" className="rounded-lg data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-md text-xs md:text-sm font-medium px-2.5 md:px-3 py-1.5">
              <Bell className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-1.5" /> Reminders
            </TabsTrigger>
            <TabsTrigger value="destinations" className="rounded-lg data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-md text-xs md:text-sm font-medium px-2.5 md:px-3 py-1.5">
              <MapPin className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-1.5" /> Destinations
            </TabsTrigger>
            <TabsTrigger value="shipping" className="rounded-lg data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-md text-xs md:text-sm font-medium px-2.5 md:px-3 py-1.5">
              <Truck className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-1.5" /> Shipping {shippingRequests.length > 0 && `(${shippingRequests.length})`}
            </TabsTrigger>
            <TabsTrigger value="flights" className="rounded-lg data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-md text-xs md:text-sm font-medium px-2.5 md:px-3 py-1.5" onClick={fetchFlightTracking}>
              <Plane className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-1.5" /> Flights
            </TabsTrigger>
            <TabsTrigger value="summary" className="rounded-lg data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-md text-xs md:text-sm font-medium px-2.5 md:px-3 py-1.5" onClick={() => fetchDailySummary()}>
              <FileText className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-1.5" /> Summary
            </TabsTrigger>
            <TabsTrigger value="analytics" className="rounded-lg data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-md text-xs md:text-sm font-medium px-2.5 md:px-3 py-1.5">
              <BarChart3 className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-1.5" /> Analytics
            </TabsTrigger>
            <TabsTrigger value="calendar" className="rounded-lg data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-md text-xs md:text-sm font-medium px-2.5 md:px-3 py-1.5">
              <CalendarDays className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-1.5" /> Calendar
            </TabsTrigger>
            <TabsTrigger value="messages" className="rounded-lg data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-md text-xs md:text-sm font-medium px-2.5 md:px-3 py-1.5">
              <Inbox className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-1.5" /> Messages
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-lg data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-md text-xs md:text-sm font-medium px-2.5 md:px-3 py-1.5">
              <Settings className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-1.5" /> Settings
            </TabsTrigger>
            <TabsTrigger value="dispatch" className="rounded-lg data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-md text-xs md:text-sm font-medium px-2.5 md:px-3 py-1.5" data-testid="admin-tab-dispatch">
              <BellRing className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-1.5" /> Dispatch
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
            <BookingsTabContent
              bookings={bookings}
              drivers={drivers}
              getAuthHeaders={getAuthHeaders}
              fetchData={fetchData}
              confirmBooking={confirmBooking}
              cancelBooking={cancelBooking}
              uncancelBooking={uncancelBooking}
              undoPayment={undoPayment}
              cashPayment={cashPayment}
              verifyPayment={verifyPayment}
              sendConfirmation={sendConfirmation}
              getStatusBadge={getStatusBadge}
              statusFilter={statusFilter} setStatusFilter={setStatusFilter}
              paymentFilter={paymentFilter} setPaymentFilter={setPaymentFilter}
              passengerStatusFilter={passengerStatusFilter} setPassengerStatusFilter={setPassengerStatusFilter}
              dateFilter={dateFilter} setDateFilter={setDateFilter}
              groupFilter={groupFilter} setGroupFilter={setGroupFilter}
              driverFilter={driverFilter} setDriverFilter={setDriverFilter}
              terminalFilter={terminalFilter} setTerminalFilter={setTerminalFilter}
              sortBy={sortBy} setSortBy={setSortBy}
              getFilteredBookings={getFilteredBookings}
              hasActiveFilters={hasActiveFilters}
              clearFilters={clearFilters}
              showUploadBooking={showUploadBooking} setShowUploadBooking={setShowUploadBooking}
              uploadedText={uploadedText} setUploadedText={setUploadedText}
              parsedBooking={parsedBooking} setParsedBooking={setParsedBooking}
              parseBookingText={parseBookingText}
              submitUploadedBooking={submitUploadedBooking}
              showCreateBooking={showCreateBooking} setShowCreateBooking={setShowCreateBooking}
              customBooking={customBooking} setCustomBooking={setCustomBooking}
              createCustomBooking={createCustomBooking}
              airports={airports}
              housingUnitsAdmin={housingUnitsAdmin}
              sendMessageToPassenger={sendMessageToPassenger}
            />
          </TabsContent>

          <TabsContent value="drivers">
            <DriversTabContent
              drivers={drivers}
              showAddDriver={showAddDriver} setShowAddDriver={setShowAddDriver}
              newDriver={newDriver} setNewDriver={setNewDriver}
              addDriver={addDriver}
              deleteDriver={deleteDriver}
              toggleDriverAvailability={toggleDriverAvailability}
              refresh={fetchDrivers}
            />
          </TabsContent>

          <TabsContent value="whatsapp">
            <WhatsAppGroupsTabContent
              whatsappGroups={whatsappGroups}
              newGroup={newGroup} setNewGroup={setNewGroup}
              addWhatsAppGroup={addWhatsAppGroup}
              deleteWhatsAppGroup={deleteWhatsAppGroup}
            />
          </TabsContent>

          <TabsContent value="reminders">
            <RemindersTabContent
              upcomingTrips={upcomingTrips}
              sendingReminders={sendingReminders}
              sendAllReminders={sendAllReminders}
              sendSingleReminder={sendSingleReminder}
            />
          </TabsContent>

          <TabsContent value="destinations">
            <DestinationsTabContent
              destinations={destinations}
              isAddingDestination={isAddingDestination} setIsAddingDestination={setIsAddingDestination}
              newDestination={newDestination} setNewDestination={setNewDestination}
              addDestination={addDestination}
              deleteDestination={deleteDestination}
            />
          </TabsContent>

          <TabsContent value="shipping">
            <ShippingTabContent 
              shippingRequests={shippingRequests}
              updateShippingStatus={updateShippingStatus}
              shippingPrices={shippingPrices}
              bolSubmissions={bolSubmissions}
            />
          </TabsContent>

          <TabsContent value="flights">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold">Live Flight Tracking</h3>
                <Button className="bg-blue-600 hover:bg-blue-700 text-white" size="sm" onClick={fetchFlightTracking} disabled={flightLoading} data-testid="refresh-flights-btn">
                  <Plane className="w-4 h-4 mr-2" />
                  {flightLoading ? "Loading..." : "Refresh Flights"}
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">Showing live status for all flight numbers in active bookings. Click "Refresh Flights" to fetch latest data from AviationStack.</p>
              {flightTracking.length === 0 ? (
                <div className="space-y-4">
                  {/* Show booked flights from booking data even without live tracking */}
                  {bookings.filter(b => b.flight_number && b.booking_status !== "cancelled").length > 0 ? (
                    <div className="space-y-3">
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                        <p className="text-sm text-amber-800 font-medium">Flight data not yet loaded. Click "Refresh Flights" above to fetch live status.</p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {[...new Set(bookings.filter(b => b.flight_number && b.booking_status !== "cancelled").map(b => b.flight_number.toUpperCase()))].map(fn => {
                          const passengers = bookings.filter(b => b.flight_number?.toUpperCase() === fn && b.booking_status !== "cancelled");
                          return (
                            <Card key={fn} className="border shadow-sm">
                              <CardContent className="p-4">
                                <div className="flex items-center justify-between mb-2">
                                  <p className="font-bold text-lg">{fn}</p>
                                  <Badge variant="secondary" className="text-xs">{passengers.length} pax</Badge>
                                </div>
                                <p className="text-xs text-muted-foreground">{passengers[0]?.airline || "Unknown Airline"}</p>
                                <p className="text-xs text-muted-foreground mt-1">{passengers[0]?.travel_date} | {passengers[0]?.pickup_location}</p>
                                <div className="mt-2 space-y-1">
                                  {passengers.map(p => (
                                    <p key={p.id} className="text-xs">{p.full_name}</p>
                                  ))}
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <Plane className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p className="text-lg font-medium">No flights to track</p>
                      <p className="text-sm">No active bookings have flight numbers</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {flightTracking.map((flight, i) => (
                    <FlightCard key={i} flight={flight} />
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="summary">
            <DailySummaryTab
              summary={dailySummary}
              loading={summaryLoading}
              onFetch={fetchDailySummary}
              onSendWhatsApp={sendDailySummaryWhatsApp}
            />
          </TabsContent>

          <TabsContent value="analytics">
            <AnalyticsTab bookings={bookings} />
          </TabsContent>

          <TabsContent value="calendar">
            <CalendarTab bookings={bookings} onViewBooking={(booking) => { setActiveTab("bookings"); }} />
          </TabsContent>

          <TabsContent value="messages">
            <MessagesTab getAuthHeaders={getAuthHeaders} />
          </TabsContent>

          <TabsContent value="settings">
            <SettingsTab getAuthHeaders={getAuthHeaders} dashFeatures={dashFeatures} setDashFeatures={setDashFeatures} />
          </TabsContent>

          <TabsContent value="dispatch">
            <DispatchersTab API={API} getAuthHeaders={getAuthHeaders} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
